import { defineStore } from 'pinia';
import axios from 'axios';
import {
  ConfigurableInsight,
  type EnrichmentValues,
  type EnrichmentValuesApiResponse,
  type BacktestJob,
  type BacktestResult,
} from '@/types/decision-engine/configurable-insights.types';
import { transformEnrichmentValues } from '@/utils/decision-engine/configurable-insights';

interface ConfigurableInsightsState {
  configurableInsights: ConfigurableInsight[];
  selectedConfigurableInsight: ConfigurableInsight | null;
  loading: boolean;
  error: string | null;
  transactionEnrichmentValues: EnrichmentValues | null;
  transactionEnrichmentValuesLoading: boolean;
  transactionEnrichmentValuesError: string | null;
}

export const useConfigurableInsightsStore = defineStore(
  'configurable-insights',
  {
    state: (): ConfigurableInsightsState => ({
      configurableInsights: [],
      selectedConfigurableInsight: null,
      loading: false,
      error: null,
      transactionEnrichmentValues: null,
      transactionEnrichmentValuesLoading: false,
      transactionEnrichmentValuesError: null,
    }),

    getters: {
      getConfigurableInsights: (state) => state.configurableInsights,
      getCurrentInsight: (state) => state.selectedConfigurableInsight,
      isLoading: (state) => state.loading,
      getError: (state) => state.error,
      getTransactionEnrichmentValues: (state) =>
        state.transactionEnrichmentValues,
      isTransactionEnrichmentValuesLoading: (state) =>
        state.transactionEnrichmentValuesLoading,
      getTransactionEnrichmentValuesError: (state) =>
        state.transactionEnrichmentValuesError,
      getTransactionCategories: (state) =>
        state.transactionEnrichmentValues?.transaction_categories || [],
      getTransactionMethods: (state) =>
        state.transactionEnrichmentValues?.transaction_methods || [],
      getPaymentProcessors: (state) =>
        state.transactionEnrichmentValues?.payment_processors || [],
      getConfigurableInsightsByUuid: (state) => {
        const map: Record<string, ConfigurableInsight> = {};
        state.configurableInsights.forEach((insight) => {
          if (insight.uuid) {
            map[insight.uuid] = insight;
          }
        });
        return map;
      },
    },

    actions: {
      async generateConfigurableInsight(prompt: string) {
        this.loading = true;
        this.error = null;
        try {
          const response = await axios.post(
            '/api/v2/agents/configurable-risk-signals/generate',
            { description: prompt },
          );
          return response.data?.data || response.data;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to generate insight';
          this.error = message;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      async saveConfigurableInsight(insight: ConfigurableInsight) {
        this.loading = true;
        this.error = null;
        try {
          const response = await axios.post(
            '/api/v2/agents/configurable-risk-signal/',
            insight,
          );
          const savedInsight = response.data;
          this.configurableInsights.push(savedInsight);
          return savedInsight;
        } catch (error: unknown) {
          const message =
            error instanceof Error ? error.message : 'Failed to save insight';
          this.error = message;
          throw error;
        } finally {
          this.loading = false;
        }
      },

      async fetchConfigurableInsights() {
        this.loading = true;
        this.error = null;
        try {
          const response = await axios.get(
            '/api/v2/agents/configurable-risk-signals/',
          );
          const data = response?.data?.data || response?.data || {};

          if (Array.isArray(data)) {
            this.configurableInsights = data;
          } else {
            // Convert object to array - use key as uuid
            this.configurableInsights = Object.keys(data).map((key) => ({
              uuid: key,
              signal_name: key,
              ...data[key],
            }));
          }
          return this.configurableInsights;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to fetch configurable insights';
          this.error = message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      async fetchTransactionEnrichmentValues() {
        // Return cached values if already loaded
        if (this.transactionEnrichmentValues) {
          return this.transactionEnrichmentValues;
        }

        this.transactionEnrichmentValuesLoading = true;
        this.transactionEnrichmentValuesError = null;
        try {
          const response = await axios.get<
            EnrichmentValuesApiResponse | { data: EnrichmentValuesApiResponse }
          >('/api/v2/transactions/enrichment_values');
          const rawData =
            'data' in response.data ? response.data.data : response.data;
          this.transactionEnrichmentValues = transformEnrichmentValues(rawData);
          return this.transactionEnrichmentValues;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to fetch transaction enrichment values';
          this.transactionEnrichmentValuesError = message;
          throw error;
        } finally {
          this.transactionEnrichmentValuesLoading = false;
        }
      },

      setSelectedConfigurableInsight(insight: ConfigurableInsight | null) {
        this.selectedConfigurableInsight = insight;
      },

      async updateConfigurableInsight(insight: ConfigurableInsight) {
        this.error = null;
        try {
          if (!insight.uuid) {
            throw new Error('Cannot update insight: no uuid found');
          }

          const response = await axios.put(
            `/api/v2/agents/configurable-risk-signal/${insight.uuid}`,
            insight,
          );
          const updatedInsight = response.data.data || response.data;

          // Update the insight in the store - merge with existing to preserve all fields
          const index = this.configurableInsights.findIndex(
            (s) => s.uuid === insight.uuid,
          );
          if (index > -1) {
            this.configurableInsights[index] = {
              ...this.configurableInsights[index],
              ...updatedInsight,
            };
          }
          return updatedInsight;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to update configurable insight';
          this.error = message;
          throw error;
        }
      },

      async deleteConfigurableInsight(insight: ConfigurableInsight) {
        this.error = null;
        try {
          if (!insight.uuid) {
            throw new Error('Cannot delete insight: no uuid found');
          }

          await axios.delete(
            `/api/v2/agents/configurable-risk-signal/${insight.uuid}`,
          );

          // Remove the insight from the store
          this.configurableInsights = this.configurableInsights.filter(
            (s) => s.uuid !== insight.uuid,
          );
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to delete configurable insight';
          this.error = message;
          throw error;
        }
      },

      async createBackTestJob(
        insight: ConfigurableInsight,
      ): Promise<BacktestJob> {
        this.error = null;
        try {
          const payload = {
            signal_config: insight.configuration,
            ...(insight.uuid && { signal_uuid: insight.uuid }),
          };
          const response = await axios.post(`/api/v2/backtests/`, payload);
          return response.data;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to create backtest job';
          this.error = message;
          throw error;
        }
      },

      async getBacktestResults(backtestId: string): Promise<BacktestResult> {
        this.error = null;
        try {
          const response = await axios.get(
            `/api/v2/backtests/${backtestId}/progress`,
          );
          return response.data;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to fetch backtest results';
          this.error = message;
          throw error;
        }
      },

      async generateNameAndDescription(insight: ConfigurableInsight) {
        try {
          const response = await axios.post(
            '/api/v2/agents/configurable-risk-signals/generate-name-and-description/',
            { configuration: insight.configuration },
          );
          return response.data?.data || response.data;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to generate name and description';
          this.error = message;
          throw error;
        }
      },

      async getBacktestsForSignal(
        signalUuid: string,
      ): Promise<BacktestResult[]> {
        try {
          const response = await axios.get(`/api/v2/backtests/`, {
            params: { signal_uuid: signalUuid },
          });
          const data = response?.data?.data ?? response?.data ?? [];
          return Array.isArray(data) ? data : [];
        } catch {
          return [];
        }
      },
    },
  },
);
